import { SITE_URL } from "utils/constant";
import {
  get,
  template,
  omitBy,
  isNull,
  isNumber,
  isBoolean,
  isEmpty,
  isString,
  compact,
  isFunction,
} from "utils/lodash";

const helper = {
  normalizeParams: (object: any) =>
    omitBy(object, (val: any) => {
      return (
        val === "" ||
        val === undefined ||
        isNull(val) ||
        (!isNumber(val) && !isBoolean(val) && isEmpty(val))
      );
    }),
  sleep: (ms: number) => new Promise((resolve) => setTimeout(resolve, ms)),
  interpolateContent: (content: any, attributes: any) => {
    return template(content, {
      interpolate: /{{([\s\S]+?)}}/g,
    })(attributes);
  },
  // Khu vực của shop = đoạn ngay trước tỉnh/thành trong displayedAddress
  // ("154/11 Nguyễn Trọng Tuyển, Phú Nhuận, Hồ Chí Minh" → "Phú Nhuận").
  shopArea: (data?: any): string => {
    const parts = String(get(data, "displayedAddress", "") || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    if (parts.length < 3) return "";
    const area = parts[parts.length - 2];
    // Bỏ nếu đoạn đó là số nhà/tên đường (có chữ số mà không phải "Quận 7")
    if (/\d/.test(area) && !/^(Quận|Q\.?)\s*\d+$/i.test(area)) return "";
    return area;
  },
  // Title SEO trang shop khi CMS không nhập meta.title:
  // "[Tên] – [Loại hình] [Khu vực] | Giá, review, đặt lịch" (rút gọn nếu > 65 ký tự)
  buildShopTitle: (data?: any): string => {
    const name = String(get(data, "name", "") || "").trim();
    if (!name) return "";
    const cat = String(get(data, "mainShopCategory.name", "") || "").trim();
    const area = helper.shopArea(data);
    const loc = [cat, area].filter(Boolean).join(" ");
    const candidates = [
      `${name}${loc ? ` – ${loc}` : ""} | Giá, review, đặt lịch`,
      `${name}${loc ? ` – ${loc}` : ""} | Lookme`,
      `${name}${area ? ` – ${area}` : ""} | Lookme`,
      name,
    ];
    return candidates.find((t) => t.length <= 65) || name;
  },
  // Meta description ≤160 ký tự, cắt ở ranh giới từ (Google cắt ~155-160).
  clampDescription: (text: string, max = 160) => {
    const t = String(text || "").replace(/\s+/g, " ").trim();
    if (t.length <= max) return t;
    const cut = t.slice(0, max - 1);
    return cut.slice(0, Math.max(cut.lastIndexOf(" "), max - 20)).replace(/[\s,.;:–-]+$/, "") + "…";
  },
  formatShopMetatags: (data?: any, metas?: any) => {
    const title =
      get(data, "meta.title") ||
      helper.buildShopTitle(data) ||
      "Lookme - Thông tin review và đặt lịch làm đẹp hơn 7000 địa điểm";
    // Mô tả riêng cho TỪNG shop (tên + loại hình + địa chỉ + điểm) để tránh
    // ~1.000 trang shop dùng chung một description generic (duplicate meta).
    const shopName = get(data, "name", "");
    const catName = get(data, "mainShopCategory.name", "");
    const addr =
      get(data, "displayedAddress", "") || get(data, "oldStreetAddress", "");
    // Điểm trong description phải làm tròn 1 chữ số — rating thô từ BE là trung
    // bình cộng chưa tròn (vd 8.095238095238095) nhìn rất xấu trên Google.
    const ratingRaw = Number(get(data, "rating"));
    const ratingVal = ratingRaw > 0 ? ratingRaw.toFixed(1) : "";
    const autoDescription = shopName
      ? [
          `${shopName}${catName ? ` - ${catName}` : ""}${addr ? ` tại ${addr}` : ""}.`,
          ratingVal ? `Đánh giá ${ratingVal}/10.` : "",
          "Xem bảng giá dịch vụ, hình ảnh, review khách hàng và đặt lịch online nhanh trên Lookme.",
        ]
          .filter(Boolean)
          .join(" ")
      : "";
    const description = helper.clampDescription(
      get(data, "meta.description") ||
      autoDescription ||
      "Lookme là nền tảng đặt lịch làm đẹp bao gồm spa, massage, làm tóc, làm móng, gội đầu, phẫu thuật thẩm mỹ. Với hơn 7000 địa điểm có review, bình luận, hình ảnh",
    );
    const _metas = {
      // Trang shop chưa có nội dung tiếng Anh → tạm ẩn bản /en (chị Naomi chốt 05/11)
      viOnly: true,
      canonicalPath: get(data, "slug") ? `/${get(data, "slug")}` : undefined,
      title,
      description,
      images: [
        {
          url: helper.imageUrl(get(data, "avatar.url")),
          width: 1200,
          height: 630,
        },
      ],
      openGraph: {
        title,
        description,
        images: [
          {
            url: helper.imageUrl(get(data, "avatar.url")),
            width: 1200,
            height: 630,
          },
        ],
        url: `${SITE_URL}/${get(
          data,
          "slug",
        )}`,
        locale: "vi_VN",
        type: "website",
      },
      twitter: {
        card: "summary_large_image",
        title,
        description,
        images: [
          {
            url: helper.imageUrl(get(data, "avatar.url")),
            width: 1200,
            height: 630,
          },
        ],
      },
    };
    return {
      ..._metas,
      ...metas,
    };
  },
  formatHomeMetatags: (data?: any, metas?: any) => {
    const title =
      get(data, "meta.title") ||
      get(data, "name") ||
      "Lookme - Đặt lịch spa, nail, gội đầu, làm tóc gần bạn";
    const description =
      get(data, "meta.description") ||
      "Lookme là nền tảng đặt lịch làm đẹp bao gồm spa, massage, làm tóc, làm móng, gội đầu, phẫu thuật thẩm mỹ. Với hơn 7000 địa điểm có review, bình luận, hình ảnh";
    const baseUrl = SITE_URL;
    const ogImageUrl = `${baseUrl}/banner.jpg`;
    const _metas = {
      // Trang nào truyền canonicalPath riêng (vd /uu-dai) thì tôn trọng, mặc định là trang chủ
      canonicalPath: get(data, "canonicalPath") || "/",
      title,
      description,
      metadataBase: new URL(baseUrl),
      images: [
        {
          url: ogImageUrl,
          width: 1200,
          height: 630,
        },
      ],
      keywords: [
        "làm đẹp",
        "spa",
        "massage",
        "salon tóc",
        "làm móng",
        "đặt lịch",
        "review",
        "Lookme",
      ],
      openGraph: {
        title,
        description,
        url: baseUrl,
        siteName: "Lookme",
        locale: "vi_VN",
        type: "website",
        images: [
          {
            url: ogImageUrl,
            width: 1200,
            height: 630,
            alt: "Lookme - Nền tảng đặt lịch làm đẹp hàng đầu Việt Nam",
          },
        ],
      },
      twitter: {
        card: "summary_large_image",
        title,
        description,
        images: [
          {
            url: ogImageUrl,
            alt: "Lookme - Nền tảng đặt lịch làm đẹp hàng đầu Việt Nam",
          },
        ],
      },
      other: {
        "google-site-verification":
          "CK0IOdgIpSiWqlamRJUjJyjbTtJXS2ES5I4aNEF9vdk",
      },
    };
    return {
      ..._metas,
      ...metas,
    };
  },
  formatMetatags: (
    _data?: any,
    type: "home" | "shop" | "service" | "campaignPackage" = "shop",
    locale: "vi" | "en" = "vi",
  ) => {
    const handler: any = {
      shop: () => helper.formatShopMetatags(_data),
      home: () => helper.formatHomeMetatags(_data),
    };
    const data = isFunction(handler[type]) ? handler[type]() : {};

    const baseUrl = SITE_URL;
    const canonicalPath = get(data, "canonicalPath");

    // Cùng một nội dung có 2 địa chỉ (VI không tiền tố, EN thêm /en). Phải khai
    // báo hreflang để Google hiểu đây là 2 bản ngôn ngữ của một trang, chứ
    // không phải 2 trang trùng lặp — và phục vụ đúng bản cho từng người tìm.
    const alternates = canonicalPath
      ? (() => {
          const clean = canonicalPath === "/" ? "" : canonicalPath;
          const viUrl = `${baseUrl}${clean}`;
          const enUrl = `${baseUrl}/en${clean}`;
          if (get(data, "viOnly")) {
            return { canonical: viUrl, languages: { vi: viUrl, "x-default": viUrl } };
          }
          return {
            canonical: locale === "en" ? enUrl : viUrl,
            languages: {
              vi: viUrl,
              en: enUrl,
              "x-default": viUrl,
            },
          };
        })()
      : undefined;

    return {
      metadataBase: new URL(baseUrl),
      ...(alternates ? { alternates } : {}),
      // Bản /en của trang chỉ-tiếng-Việt: noindex, follow (canonical đã trỏ về VI)
      ...(get(data, "viOnly") && locale === "en"
        ? { robots: { index: false, follow: true } }
        : {}),
      title: get(data, "title"),
      description: get(data, "description"),
      images: get(data, "images"),
      openGraph: get(data, "openGraph")
        ? {
            ...get(data, "openGraph"),
            // og:locale phải theo ngôn ngữ đang xem để Facebook/Zalo hiện đúng bản
            locale: locale === "en" ? "en_US" : "vi_VN",
            ...(get(data, "canonicalPath")
              ? {
                  url: `${baseUrl}${locale === "en" ? "/en" : ""}${
                    get(data, "canonicalPath") === "/"
                      ? ""
                      : get(data, "canonicalPath")
                  }`,
                }
              : {}),
          }
        : undefined,
      twitter: get(data, "twitter"),
      icons: {
        icon: [
          {
            url: `/favicon-16x16.png`,
            sizes: "16x16",
            type: "image/png",
          },
          {
            url: `/favicon-32x32.png`,
            sizes: "32x32",
            type: "image/png",
          },
          {
            url: `/favicon.ico`,
            sizes: "any",
          },
        ],
        shortcut: `/logo-192x192.png`,
        apple: `/apple-touch-icon.png`,
      },
      manifest: `/site.webmanifest`,
      other: get(data, "other"),
    };
  },
  formatShop: (shop: any) => {
    return {
      ...shop,

      thumbnail:
        get(shop, "avatar.sizes.thumbnail.url") ||
        get(shop, "avatar.sizes.card.url") ||
        get(shop, "avatar.url"),
    };
  },
  imageUrl: (item?: string | object) => {
    const url = isString(item) ? item : get(item, "url", "");
    if (url === "") return "";

    // Nếu URL đã là absolute URL, trả về nguyên vẹn
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    // Nếu URL đã chứa domain của IMAGE_URL, trả về nguyên vẹn
    if (url.includes(process.env.NEXT_PUBLIC_IMAGE_URL || "")) {
      return url;
    }

    // Thêm domain vào URL relative
    const baseUrl = process.env.NEXT_PUBLIC_IMAGE_URL || "";
    return baseUrl ? `${baseUrl}${url.startsWith("/") ? url : `/${url}`}` : url;
  },
  lexicalToString: (item?: string | object) => {
    const url = isString(item) ? item : get(item, "url", "");
    if (url === "") return "";

    // Nếu URL đã là absolute URL, trả về nguyên vẹn
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    // Nếu URL đã chứa domain của IMAGE_URL, trả về nguyên vẹn
    if (url.includes(process.env.NEXT_PUBLIC_IMAGE_URL || "")) {
      return url;
    }

    // Thêm domain vào URL relative
    const baseUrl = process.env.NEXT_PUBLIC_IMAGE_URL || "";
    return baseUrl ? `${baseUrl}${url.startsWith("/") ? url : `/${url}`}` : url;
  },
  callAll: async (...promises: (() => Promise<any>)[]) => {
    try {
      const results = await Promise.all(
        promises.map((promise, index) =>
          promise().catch((error) => {
            throw new Error(`Error in promise [${index}]: ${error.message}`);
          }),
        ),
      );
      return results;
    } catch (error: any) {
      console.error("Error in callAll:", error.message);
      throw error;
    }
  },
};

export default helper;
